import { faPauseCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext, useMemo } from 'react';

import useTreatabilityInsight from '@/hooks/useTreatabilityInsight';
import PersonContext from '@/pages/Person/PersonContext';
import PaymentRow from '@/pages/Profile/components/PaymentRow';
import SectionHeading from '@/pages/Profile/components/SectionHeading';
import type { Phase } from '@/types/phase';
import IconItem from '@/ui/components/IconItem';
import Spinner from '@/ui/components/Spinner';
import Tooltip from '@/ui/components/Tooltip';
import UnknownIconItem from '@/ui/components/UnknownIconItem';
import {
  formatISOStringToReadableDate,
  daysBetween,
  parseIsoDate,
  getAge,
  dateFromDateStringWithAppendedTime
} from '@/utils/dates';

const TreatabilitySection = () => {
  const {
    person,
    person: { classifications }
  } = useContext(PersonContext);
  const { data: treatabilityInsight, isPending } = useTreatabilityInsight(person.id);

  const birthday = useMemo(
    () => (person.birthday ? parseIsoDate(person.birthday) : undefined),
    [person.birthday]
  );
  const age = useMemo(() => (birthday ? getAge(birthday) : '?'), [birthday]);

  const currentPhase = useMemo((): Phase | undefined => {
    return person?.episode_of_care?.phases?.find((phase) => phase.is_current);
  }, [person.episode_of_care?.phases]);

  const inEligibleClassification = useMemo(
    () => person?.classifications.find((c: string) => c === 'insurance.ineligible'),
    [person?.classifications]
  );

  const defaultInsurance = useMemo(() => {
    return (
      person.insurance_policies.find((policy) => policy.is_default) || person.insurance_policies[0]
    );
  }, [person.insurance_policies]);

  const renderWaitlisted = () => {
    if (isPending) {
      return (
        <div>
          <Spinner label="Loading Waitlisted..." />
        </div>
      );
    }

    if (treatabilityInsight?.data?.determination) {
      const { treatability_match_active_on } = treatabilityInsight.data.determination;

      if (treatability_match_active_on) {
        const isInTheFuture =
          new Date(treatability_match_active_on).getTime() > new Date().getTime();

        return isInTheFuture ? (
          <>
            <FontAwesomeIcon icon={faPauseCircle} className="text-orange-600 mr-2" />
            <strong>Waitlisted Until:</strong>{' '}
            {dateFromDateStringWithAppendedTime(treatability_match_active_on).toLocaleString(
              'en-US',
              {
                day: 'numeric',
                month: '2-digit',
                year: '2-digit'
              }
            )}
            <hr />
          </>
        ) : null;
      }

      return null;
    }

    return null;
  };

  const renderProgram = useMemo(() => {
    const checked =
      person.program?.active &&
      !classifications.find((classification) => classification === 'program.not_active');

    return (
      <div data-testid={`treatability-program-icon-${checked}`}>
        <IconItem checked={checked}>
          <a className="text-blue-600" href="#program-qualification-section">
            Program:{' '}
          </a>
          <Tooltip
            tooltipContent={`Program start: ${
              person.episode_of_care?.admitted_at
                ? formatISOStringToReadableDate(person.episode_of_care?.admitted_at)
                : 'No EOC admitted_at'
            }`}>
            {person.program?.name} ({person?.program_qualification?.primary_substance || '?'}):{' '}
            <div>{person.episode_of_care?.treatment_days_and_weeks}</div>
          </Tooltip>
          {currentPhase ? (
            <Tooltip
              tooltipContent={
                currentPhase?.start_at ? (
                  <div>
                    <div>Phase start: {formatISOStringToReadableDate(currentPhase.start_at)}</div>
                    <div>{daysBetween(new Date(currentPhase.start_at))} days</div>
                  </div>
                ) : (
                  'No phase start_at'
                )
              }>
              {currentPhase?.phase_type.name}
            </Tooltip>
          ) : (
            'No current phase'
          )}
        </IconItem>
      </div>
    );
  }, [
    classifications,
    currentPhase,
    person.episode_of_care?.admitted_at,
    person.episode_of_care?.treatment_days_and_weeks,
    person.program?.active,
    person.program?.name,
    person?.program_qualification?.primary_substance
  ]);

  return (
    <div>
      <SectionHeading text="Treatability" />

      {renderWaitlisted()}

      {/* Jurisdiction */}
      {person.jurisdiction ? (
        <IconItem checked={!!person.jurisdiction?.active_on}>
          <a className="text-blue-600" href="#basic-info-section">
            {person.jurisdiction?.name}
          </a>
        </IconItem>
      ) : (
        <UnknownIconItem>
          <a className="text-blue-600" href="#basic-info-section">
            No Jurisdiction
          </a>
        </UnknownIconItem>
      )}

      {/* Program */}
      {person.program ? (
        renderProgram
      ) : (
        <UnknownIconItem>
          No program <span>({person?.program_qualification?.primary_substance || '?'})</span>
        </UnknownIconItem>
      )}

      {/* Payment Type */}
      {person.payment_type && (
        <PaymentRow
          payment_type={person.payment_type}
          insurance={defaultInsurance}
          insuranceIneligible={!!inEligibleClassification}
        />
      )}

      {/* Birthday */}
      {person.birthday ? (
        <IconItem checked={age && age !== '?' && age >= 18 ? true : false}>
          <a className="text-blue-600" href="#basic-info-section">
            Birthday:{' '}
          </a>
          {birthday &&
            birthday.toLocaleString('en-us', {
              month: 'short',
              year: 'numeric',
              day: 'numeric'
            })}{' '}
          ({age}yrs)
        </IconItem>
      ) : (
        <UnknownIconItem>
          <a className="text-blue-600" href="#basic-info-section">
            Birthday unknown
          </a>
        </UnknownIconItem>
      )}
    </div>
  );
};

export default memo(TreatabilitySection);
